import { Box, Grid, GridItem, Heading, Text } from '@chakra-ui/react'
import { DashboardLoading } from 'components/atoms/DashboardLoading'
import { DashboardNotFound } from 'components/atoms/DashboardNotFound'
import { CustomBarChart } from 'components/molecules/CustomBarChart'
import { CustomPieChart } from 'components/molecules/CustomPieChart'
import { useInstitutionsContext } from 'contexts/institutions'
import { memo, useEffect, useState } from 'react'
import { getFinanceDashboardData } from 'services/dashboard'
import formatter from 'utils/formatter'
import { useDebounce } from 'utils/useDebounce'
import { FinanceDashboardResponseDTO, FinanceFilterDTO } from '../../types'

interface PropsModeDashboard {
    filter: FinanceFilterDTO
    idDashboard: string
}

const ModeDashboard: React.FC<PropsModeDashboard> = memo(
    ({ filter, idDashboard }) => {
        const [dashboardData, setDashboardData] =
            useState<FinanceDashboardResponseDTO>({
                dados: []
            })
        const [loading, setLoading] = useState<boolean>(true)
        const { currentInstitution, currentAgent } = useInstitutionsContext()

        const fetchDashboard = async () => {
            try {
                setLoading(true)
                const response = await getFinanceDashboardData(
                    { ...filter, cnpjAgente: currentAgent?.cnpj ?? '' },
                    currentInstitution?.id
                )
                if (response?.data) {
                    setDashboardData(response.data)
                } else {
                    console.warn('Formato inesperado da resposta:', response)
                    setDashboardData({ dados: [] })
                }
            } catch (error) {
                console.error('Erro ao buscar dados do dashboard:', error)
                setDashboardData({ dados: [] })
            } finally {
                setLoading(false)
            }
        }

        const bounceAPI = useDebounce(fetchDashboard, 1000)

        useEffect(() => {
            setLoading(true)
            bounceAPI()
        }, [filter, currentAgent?.cnpj, currentInstitution?.id])

        const buildDashboards = () => {
            if (!dashboardData?.dados?.length) return

            return dashboardData?.dados.map(item => ({
                ...item,
                values: item.values.map(value => ({
                    ...value,
                    nome: value.nome
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '')
                        .toUpperCase(),
                    totalRegistroEAditivo: value.aditivo + value.registro,
                    quantidadeRegistroEAditivo:
                        value.quantidadeAditivo + value.quantidadeRegistro
                }))
            }))
        }

        const CustomTooltipValues = ({ active, payload }: any) => {
            if (active && payload && payload.length) {
                const { nome, aditivo, registro, totalRegistroEAditivo } =
                    payload[0].payload
                const isShowRegistro = registro > 0
                const isShowAditivo = aditivo > 0
                return (
                    <Box
                        bg="white"
                        border="1px solid #ddd"
                        p={2}
                        borderRadius="md"
                        shadow="sm"
                    >
                        <Text fontWeight="bold">{nome}</Text>

                        <Box
                            display={isShowRegistro ? 'flex' : 'none'}
                            gap="4px"
                            justifyContent="space-between"
                        >
                            <Text>Total de Registro(s): </Text>
                            <Text fontWeight="bold">
                                {formatter.moeda(registro)}
                            </Text>
                        </Box>
                        <Box
                            display={isShowAditivo ? 'flex' : 'none'}
                            gap="4px"
                            justifyContent="space-between"
                        >
                            <Text>Total de Aditivo(s): </Text>
                            <Text fontWeight="bold">
                                {formatter.moeda(aditivo)}
                            </Text>
                        </Box>
                        <Box
                            display="flex"
                            gap="4px"
                            justifyContent="space-between"
                        >
                            <Text>Valor Total: </Text>
                            <Text fontWeight="bold">
                                {' '}
                                {formatter.moeda(totalRegistroEAditivo)}
                            </Text>
                        </Box>
                    </Box>
                )
            }
            return null
        }

        const CustomTooltipQuantity = ({ active, payload }: any) => {
            if (active && payload && payload.length) {
                const { nome, quantidadeAditivo, quantidadeRegistro } =
                    payload[0].payload
                const isShowRegistro = quantidadeRegistro > 0
                const isShowAditivo = quantidadeAditivo > 0
                return (
                    <Box
                        bg="white"
                        border="1px solid #ddd"
                        p={2}
                        borderRadius="md"
                        shadow="sm"
                    >
                        <Text fontWeight="bold">{nome}</Text>
                        <Box
                            display={isShowRegistro ? 'flex' : 'none'}
                            gap="4px"
                            justifyContent="space-between"
                        >
                            <Text>Quantidade de Registro(s): </Text>
                            <Text fontWeight="bold">
                                {formatter.formatWithLeadingZero(
                                    quantidadeRegistro
                                )}
                            </Text>
                        </Box>
                        <Box
                            display={isShowAditivo ? 'flex' : 'none'}
                            justifyContent="space-between"
                        >
                            <Text>Quantidade de Aditivo(s): </Text>
                            <Text fontWeight="bold">
                                {formatter.formatWithLeadingZero(
                                    quantidadeAditivo
                                )}
                            </Text>
                        </Box>
                    </Box>
                )
            }
            return null
        }

        if (loading) {
            return <DashboardLoading />
        }

        return (
            <>
                <div id={idDashboard}>
                    {dashboardData?.dados?.length > 0 ? (
                        <Grid
                            templateColumns="repeat(auto-fit, minmax(600px, 1fr))"
                            gap={6}
                        >
                            {buildDashboards()?.map((item, index) => (
                                <>
                                    <GridItem key={index}>
                                        <Box
                                            bg="white"
                                            borderRadius="md"
                                            shadow="lg"
                                            p={6}
                                            transition="all 0.3s"
                                            _hover={{ shadow: 'xl' }}
                                        >
                                            <Heading
                                                size="md"
                                                marginY={8}
                                                textAlign="center"
                                            >
                                                {item.nome} - Total de Registros
                                                e Aditivos (R$)
                                            </Heading>
                                            <CustomBarChart
                                                children={CustomTooltipValues}
                                                dataKeyBar="totalRegistroEAditivo"
                                                dataKeyYAxis="nome"
                                                values={item.values}
                                            />
                                        </Box>
                                    </GridItem>
                                    <GridItem key={index}>
                                        <Box
                                            bg="white"
                                            borderRadius="md"
                                            shadow="lg"
                                            p={6}
                                            transition="all 0.3s"
                                            _hover={{ shadow: 'xl' }}
                                        >
                                            <Heading
                                                size="md"
                                                marginY={8}
                                                textAlign="center"
                                            >
                                                {item.nome} - Quantidade de
                                                Registros e Aditivos
                                            </Heading>

                                            <CustomPieChart
                                                dataKey="quantidadeRegistroEAditivo"
                                                outerRadius={150}
                                                values={item.values}
                                                children={CustomTooltipQuantity}
                                            />
                                        </Box>
                                    </GridItem>
                                </>
                            ))}
                        </Grid>
                    ) : (
                        <DashboardNotFound />
                    )}
                </div>
            </>
        )
    }
)

export default ModeDashboard
